import { uniqBy } from "lodash"
import moment from "moment"
import React from "react"
import { Button, Col, Dropdown, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory, { textFilter } from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
// import BootstrapTable from "react-bootstrap-table-next";
import { useTranslation } from "react-i18next"
import { ShippingRateModel } from "../../api/pricing.api"
import { useAppDispatch } from "../../app/hooks"
import aramex from "../../assets/ShippingCompaniesIcons/aramex.svg"
import dhl from "../../assets/ShippingCompaniesIcons/dhl.svg"
import elite from "../../assets/ShippingCompaniesIcons/elite.svg"
import fedex from "../../assets/ShippingCompaniesIcons/fedex.svg"
import shopini from "../../assets/ShippingCompaniesIcons/shopini-economy.svg"
import ups from "../../assets/ShippingCompaniesIcons/ups.svg"
import usps from "../../assets/ShippingCompaniesIcons/usps.svg"
import { SearchBar } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"
import { loadSurcharges } from "../../store/pricing.store"
import { countryNamesInEnglish } from "../../utils/commonFunctions"

let countryFilter: any, shippingCompanyFilter: any

function ShippingRatesTable({
    shippingRates,
    shippingRateDetails,
    setShippingRateDetails,
    setShow,
    shippingRateSearch,
    setShippingRateSearch,
    setSelectedRates,
    selectedRates,
    isFiltered,
    setIsFiltered
}: {
    shippingRates: ShippingRateModel[]
    shippingRateDetails: Record<string, any>
    setShippingRateDetails: Function
    setShow: Function
    shippingRateSearch: Record<string, any>
    setShippingRateSearch: Function
    setSelectedRates: Function
    selectedRates: number[]
    isFiltered: string
    setIsFiltered: Function
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    function shippingRateUpdatingDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }
    const onSelect = (row: Record<string, any>, isSelect: any) => {
        if (isSelect) {
            setSelectedRates([...selectedRates, row?.id])
            setIsFiltered("selected")
        } else {
            setSelectedRates(selectedRates?.filter((s) => s !== row?.id))
            if (selectedRates?.filter((s) => s !== row?.id)?.length === 0) {
                setIsFiltered("filtered")
            }
        }
    }
    const handleSelectAll = (isSelect: any, rows: Record<string, any>[]) => {
        if (isSelect) {
            setIsFiltered("selected")
            setSelectedRates(rows?.map((r) => r?.id))
        } else {
            setIsFiltered("filtered")
            setSelectedRates([])
        }
    }
    const selectRow: any = {
        mode: "checkbox",
        clickToSelect: true,
        onSelect: onSelect,
        onSelectAll: handleSelectAll,
        selectColumnPosition: "left"
    }

    const handleChange = (filter: string, option: any, extra?: any) => {
        if (extra) {
            setShippingRateSearch({
                ...shippingRateSearch,
                [filter]: option,
                shipping_name: extra
            })
        } else {
            setShippingRateSearch({
                ...shippingRateSearch,
                [filter]: option
            })
        }
    }

    const shippingRateColumns = [
        {
            text: t(""),
            dataField: "id",
            editable: false,
            sort: true
        },
        {
            text: t("Last Updated"),
            dataField: "updated_at",
            editable: false,
            formatter: (cell: string, row: Record<string, any>) => {
                return shippingRateUpdatingDate(cell)
            }
        },
        {
            text: t("Shipping"),
            dataField: "ShippingCompany",
            editable: false,
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                let imageSrc = cell?.logo
                if (cell?.logo?.match("aramex")) {
                    imageSrc = aramex
                }
                if (cell?.logo?.match("dhl")) {
                    imageSrc = dhl
                }
                if (cell?.logo?.match("FedEx")) {
                    imageSrc = fedex
                }
                if (cell?.logo?.match("usps")) {
                    imageSrc = usps
                }
                if (cell?.logo?.match("ups")) {
                    imageSrc = ups
                }
                if (cell?.logo?.match("shopini")) {
                    imageSrc = shopini
                }
                if (cell?.logo?.match("elite")) {
                    imageSrc = elite
                }
                return <img src={imageSrc} className="w-50" />
            },
            filter: textFilter({
                getFilter: (filter: any) => {
                    shippingCompanyFilter = filter
                }
            })
        },
        {
            text: t("Service"),
            dataField: "shipping_service",
            editable: false
        },
        {
            text: t("Dest"),
            dataField: "country_code",
            editable: false,
            formatter: (cell: string) => {
                return cell && cell !== "" ? (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 250, hide: 400 }}
                        overlay={<Tooltip id={`${cell}-tooltip`}>{countryNamesInEnglish.of(cell)}</Tooltip>}>
                        <span className="tr-pointer">{cell}&nbsp;</span>
                    </OverlayTrigger>
                ) : (
                    "-"
                )
            },
            filter: textFilter({
                getFilter: (filter: any) => {
                    countryFilter = filter
                }
            })
        },
        {
            text: t("Weight"),
            dataField: "weight",
            editable: false,
            formatter: (cell: number, row: Record<string, any>) => {
                return `${cell} ${row?.weight_unit}`
            },
            sort: true
        },
        {
            text: t("Base"),
            dataField: "base_charge",
            sort: true,
            formatter: (cell: number, row: Record<string, any>) => {
                return `${cell?.toFixed(2)}$`
            }
        },
        {
            text: t("Surcharge"),
            dataField: "surcharge",
            sort: true,
            formatter: (cell: number, row: Record<string, any>) => {
                return `${cell?.toFixed(2)}$`
            }
        },
        {
            text: t("Total Rates"),
            dataField: "rate",
            sort: true,
            formatter: (cell: number, row: Record<string, any>) => {
                return `${cell?.toFixed(2)}$`
            }
        },
        {
            text: t("Margin"),
            dataField: "margin",
            sort: true,
            hidden: true
        },
        {
            text: t("Applied Rates"),
            dataField: "",
            sort: true,
            formatter: (cell: number, row: Record<string, any>) => {
                return `${((parseFloat(row?.margin) * parseFloat(row?.rate)) / 100 + parseFloat(row?.rate))?.toFixed(2)}$`
            }
        },
        {
            dataField: "",
            text: t("Actions"),
            editable: false,
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Dropdown className="actions-menu">
                        <Dropdown.Toggle id="dropdown-basic" size={"sm"} className="py-0 primary-bg">
                            {t("Actions")}
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            {["Edit"].map((action) => (
                                <Dropdown.Item
                                    className="py-0"
                                    onClick={() => {
                                        dispatch(
                                            loadSurcharges({ shippingServiceId: row?.shipping_service_id })
                                        )
                                        const tempShippingRateDetails = {
                                            ...row,
                                            new_rate: (
                                                (parseFloat(row?.margin) * parseFloat(row?.rate)) / 100 +
                                                parseFloat(row?.rate)
                                            )?.toFixed(2)
                                        }
                                        setShow("shippingRateModal")
                                        setShippingRateDetails(tempShippingRateDetails)
                                    }}>
                                    {action}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }
        }
    ]

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {shippingRateColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={shippingRates}
                    columns={shippingRateColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "ShippingRatesTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4 mx-2">
                            <Row className="search shippingRates-search">
                                <Col xs={12} sm={12} md={12} lg={3}>
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Form.Select
                                        name="country"
                                        onChange={(e: any) => handleChange(e.target.name, e.target.value)}>
                                        <option value="">{t("All Countries")}</option>
                                        {uniqBy(shippingRates, "country_code")?.map((rate: any) => (
                                            <option
                                                key={rate?.country_code}
                                                selected={rate?.country_code === shippingRateSearch?.country}
                                                value={rate?.country_code}>
                                                {`${rate?.country_code} - ${countryNamesInEnglish.of(rate?.country_code || "")}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Form.Select
                                        name="shipping_company_id"
                                        onChange={(e: any) => {
                                            handleChange(
                                                e.target.name,
                                                JSON.parse(e.target.value).shipping_company_id,
                                                JSON.parse(e.target.value).shipping_name
                                            )
                                        }}>
                                        <option
                                            value={JSON.stringify({
                                                shipping_company_id: "",
                                                shipping_name: ""
                                            })}>
                                            {t("All Shipping Companies")}
                                        </option>
                                        {uniqBy(
                                            shippingRates?.map(
                                                (sr: Record<string, any>) => sr?.ShippingCompany
                                            ),
                                            "shipping_name"
                                        )?.map((sc: any) => (
                                            <option
                                                key={sc?.id}
                                                selected={sc?.id === shippingRateSearch?.shipping_company_id}
                                                value={JSON.stringify({
                                                    shipping_company_id: sc?.id,
                                                    shipping_name: sc?.shipping_name
                                                })}>
                                                {sc?.shipping_name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xs={12} sm={6} md={6} lg={2}>
                                    <Form.Select
                                        name="shipping_service"
                                        onChange={(e: any) => handleChange(e.target.name, e.target.value)}>
                                        <option value="">{t("All Services")}</option>
                                        {uniqBy(shippingRates, "shipping_service")?.map((rate: any) => (
                                            <option
                                                key={rate?.shipping_service}
                                                selected={
                                                    rate?.shipping_service ===
                                                    shippingRateSearch?.shipping_service
                                                }
                                                value={rate?.shipping_service}>
                                                {rate?.shipping_service}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col className="d-none">
                                    {/* <ExportCSVButton {...props.csvProps} id="shippingRatesExportButton">
											Export
										</ExportCSVButton> */}
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <Row className="mt-3 align-items-center justify-content-between">
                                        <Col xs={6} className="d-flex justify-content-start align-items-center">
                                            <Form.Check
                                                type="radio"
                                                label={"Selected"}
                                                disabled={shippingRates?.length === 0}
                                                checked={isFiltered === "selected"}
                                                onChange={(e: any) => {
                                                    if (e.target.checked) {
                                                        setIsFiltered("selected")
                                                    } else {
                                                        setIsFiltered("")
                                                    }
                                                }}
                                            />
                                            <Form.Check
                                                type="radio"
                                                label={"Filtered"}
                                                checked={isFiltered === "filtered"}
                                                disabled={shippingRates?.length === 0}
                                                onChange={(e: any) => {
                                                    if (e.target.checked) {
                                                        setIsFiltered("filtered")
                                                    } else {
                                                        setIsFiltered("")
                                                    }
                                                }}
                                                className={"mx-4"}
                                            />
                                        </Col>
                                        <Col xs={6} className="d-flex justify-content-end align-items-center">
                                            <Button
                                                className={isFiltered === "" ? "grey-bg" : "primary-bg"}
                                                onClick={() => setShow("bulkUpdateModal")}
                                                disabled={isFiltered === "" || shippingRates?.length === 0}>
                                                {t("Update Rates")}
                                            </Button>
                                        </Col>
                                    </Row>
                                    <BootstrapTable
                                        {...props.baseProps}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        selectRow={selectRow}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default ShippingRatesTable
